import React from 'react';
import { Box, Button, Card, Skeleton, Typography, Tooltip } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { numeric, sectionCaptionSx, sectionTitleSx, surfaceSx } from './dashboardStyles';

const formatInMillions = (value) => {
  if (!value && value !== 0) return '-';
  const millions = Math.abs(value) / 1_000_000;
  // 1-digit millions: 2 decimals, 2-digit: 1 decimal, 3+ digits: none.
  const decimals = millions < 10 ? 2 : millions < 100 ? 1 : 0;
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(millions);
  const text = `$${formatted}M`;
  return value < 0 ? `(${text})` : text;
};

const formatVarInMillions = (value) => {
  if (!value && value !== 0) return '-';
  const millions = Math.abs(value) / 1_000_000;
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(millions);
  const text = `$${formatted}M`;
  return value < 0 ? `(${text})` : text;
};

const formatPercentOfNav = (value, nav) => {
  if (!value && value !== 0) return '-';
  if (!nav || nav === 0) return '-';
  const percentage = (value / nav) * 100;
  const text = `${Math.abs(percentage).toFixed(1)}%`;
  return percentage < 0 ? `(${text})` : text;
};

const ValueWithTooltip = ({ value, label = '', displayValue }) => {
  if (!value && value !== 0) {
    return <span>-</span>;
  }
  const full = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
  const fullValue = value < 0 ? `(${full})` : full;

  return (
    <Tooltip title={`${label ? label + ': ' : ''}${fullValue}`} arrow>
      <span style={{ cursor: 'help', borderBottom: '1px dotted #999' }}>
        {displayValue}
      </span>
    </Tooltip>
  );
};

export default function FirmwideSummaryRiskSection({ firmwideData, viewMode = 'millions', overLimitCount = null, strategyCount = 0, onViewTrends, varLoading = false }) {
  const aum = firmwideData?.aum ?? null;
  const totalRiskCapitalAllocated = firmwideData?.totalRiskCapitalAllocated || 0;
  const reserve = firmwideData?.reserve ?? null;
  const var95 = firmwideData?.var95 ?? null;
  const var99 = firmwideData?.var99 ?? null;

  const getDisplayValue = (value, label, isAum = false, isVaR = false) => {
    if (viewMode === 'percentage' && !isAum) {
      return formatPercentOfNav(value, aum);
    }
    const displayValue = isVaR ? formatVarInMillions(value) : formatInMillions(value);
    return (
      <ValueWithTooltip
        value={value}
        label={label}
        displayValue={displayValue}
      />
    );
  };

  const metrics = [
    {
      label: 'AUM',
      value: aum,
      color: '#1e293b',
      isAum: true,
    },
    {
      label: 'Total Risk Capital Allocated',
      value: totalRiskCapitalAllocated,
      color: '#006842',
    },
    {
      label: 'Reserve',
      value: reserve,
      color: '#1e293b',
    },
    {
      label: 'VaR 95%',
      value: var95,
      color: '#B85450',
      asOf: firmwideData?.varAsOfDate,
      isVaR: true,
    },
    {
      label: 'VaR 99%',
      value: var99,
      color: '#B85450',
      asOf: firmwideData?.varAsOfDate,
      isVaR: true,
    },
  ];

  const allWithinLimits = overLimitCount === 0;
  // VaR carries its own as-of date; show it only where it differs from the row's date.
  const rowDate = firmwideData?.asOfDate || 'N/A';

  return (
    <Card elevation={0} sx={{ ...surfaceSx, overflow: 'hidden', ...numeric }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          px: 2.5,
          py: 1,
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={sectionTitleSx}>Firmwide Risk</Typography>
          <Typography sx={sectionCaptionSx}>as of {rowDate}</Typography>
          {overLimitCount != null && strategyCount > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: allWithinLimits ? '#059669' : '#dc2626' }} />
              <Typography sx={{ ...sectionCaptionSx, color: '#334155', fontWeight: 600 }}>
                {allWithinLimits ? `All ${strategyCount} strategies within limits` : `${overLimitCount} of ${strategyCount} strategies over limit`}
              </Typography>
            </Box>
          )}
        </Box>
        {onViewTrends && (
          <Button size="small" onClick={onViewTrends} endIcon={<ArrowDownwardIcon sx={{ fontSize: '14px !important' }} />} sx={{ textTransform: 'none', fontWeight: 600, color: '#334155' }}>
            Risk Trends
          </Button>
        )}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
        }}
      >
        {metrics.map((metric, index) => (
          <Box
            key={metric.label}
            sx={{
              px: 2.5,
              py: 1.75,
              borderLeft: { md: index === 0 ? 'none' : '1px solid #e2e8f0' },
              borderTop: { xs: index > 1 ? '1px solid #e2e8f0' : 'none', md: 'none' },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '600',
                color: '#64748b',
                textTransform: metric.label.startsWith('VaR') ? 'none' : 'uppercase',
                letterSpacing: '0.5px',
                mb: 0.5,
              }}
            >
              {metric.label}
            </Typography>
            <Typography
              sx={{
                fontSize: '26px',
                fontWeight: '700',
                color: Number(metric.value) < 0 ? '#c53030' : metric.color,
                lineHeight: 1.2,
              }}
            >
              {metric.isVaR && varLoading ? (
                <Skeleton variant="text" animation="wave" width={90} />
              ) : (
                getDisplayValue(metric.value, metric.label, metric.isAum, metric.isVaR)
              )}
            </Typography>
            {metric.asOf && metric.asOf !== firmwideData?.asOfDate && (
              <Typography variant="caption" sx={{ display: 'block', fontSize: '11px', color: '#94a3b8', mt: 0.5 }}>
                as of {metric.asOf}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Card>
  );
}
